import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/authService';

export const useAdminFAQ = (initialFilters = {}) => {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState({
    is_active: '',
    category: '',
    ...initialFilters
  });

  const activeStatusOptions = [
    { value: '', label: 'All FAQs' },
    { value: 'true', label: 'Active' },
    { value: 'false', label: 'Inactive' }
  ];

  const cleanFilters = useMemo(() => {
    return Object.fromEntries(
      Object.entries(filters).filter(([_, value]) => value !== '' && value !== null && value !== undefined)
    );
  }, [filters]);

  const queryString = new URLSearchParams(cleanFilters).toString();

  const { data: faqsResponse, isLoading, error } = useQuery({
    queryKey: ['adminFAQ', cleanFilters],
    queryFn: async () => {
      const response = await api.get(`/admin/faqs${queryString ? `?${queryString}` : ''}`);
      return response.data;
    }
  });

  // Fetch statistics
  const { data: statsResponse, isLoading: isLoadingStats } = useQuery({
    queryKey: ['adminFAQStats'],
    queryFn: async () => {
      const response = await api.get('/admin/faqs/statistics');
      return response.data;
    }
  });

  const createMutation = useMutation({
    mutationFn: async (data) => {
      const response = await api.post('/admin/faqs', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['adminFAQ']);
      queryClient.invalidateQueries(['adminFAQStats']);
    }
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await api.put(`/admin/faqs/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['adminFAQ']);
      queryClient.invalidateQueries(['adminFAQStats']);
    }
  });

  const toggleStatusMutation = useMutation({
    mutationFn: async (id) => {
      const response = await api.patch(`/admin/faqs/${id}/toggle-status`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['adminFAQ']);
      queryClient.invalidateQueries(['adminFAQStats']);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      const response = await api.delete(`/admin/faqs/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['adminFAQ']);
      queryClient.invalidateQueries(['adminFAQStats']);
    }
  });

  const faqs = useMemo(() => {
    const data = faqsResponse?.data?.data || faqsResponse?.data;
    if (data && Array.isArray(data)) {
      return data;
    }
    if (data && data.data && Array.isArray(data.data)) {
      return data.data;
    }
    return [];
  }, [faqsResponse]);

  const statistics = useMemo(() => {
    return statsResponse?.data || {};
  }, [statsResponse]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSearch = (data, searchTerm) => {
    if (!searchTerm) return data;
    
    return data.filter(faq => 
      faq.question?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      faq.answer?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      faq.category?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  return {
    faqs,
    isLoading: isLoading || isLoadingStats,
    error,
    filters,
    activeStatusOptions,
    handleFilterChange,
    handleSearch,
    statistics,
    createFAQ: createMutation.mutate,
    isCreating: createMutation.isPending,
    updateFAQ: updateMutation.mutate,
    isUpdating: updateMutation.isPending,
    toggleStatus: toggleStatusMutation.mutate,
    isToggling: toggleStatusMutation.isPending,
    deleteFAQ: deleteMutation.mutate,
    isDeleting: deleteMutation.isPending
  };
};

